import styled from 'styled-components';
import { WorkTaskStatus } from '../../api/api';
import { getWorkTaskStatusColor } from '../../components/status-tag/workTaskStatusColor';
import { formatDateString } from '../../utils/dateHandling';
import { getEnumDisplayValue } from '../../utils/enumUtils';
import { DiagramNode } from './relationsLayout';

interface Props {
  node: DiagramNode;
  onClick: (workTaskId: number) => void;
}

const PADDING = 14;

/** SVG has no text overflow, so long labels are cut to what fits the node width. */
const truncate = (text: string, availableWidth: number, fontSize: number) => {
  const maxCharacters = Math.floor(availableWidth / (fontSize * 0.55));
  return text.length > maxCharacters ? `${text.substring(0, Math.max(maxCharacters - 1, 1))}…` : text;
};

const formatDates = (fromDate?: string, toDate?: string) => {
  const from = fromDate ? formatDateString(fromDate) : undefined;
  const to = toDate ? formatDateString(toDate) : undefined;

  if (from && to && from !== to) return `${from} - ${to}`;
  return from ?? to ?? 'Ingen dato';
};

const RelationNode = (props: Props) => {
  const { node, onClick } = props;
  const { task, x, y, width, height } = node;

  const statusColor = getWorkTaskStatusColor(task.status);
  const contentWidth = width - PADDING - 12;
  // The work order is left to the dashed visit box around the node, so the node shows the asset.
  const identifier = task.assetId ? `${task.id} · ${task.assetId}` : `${task.id}`;

  return (
    <Group
      transform={`translate(${x}, ${y})`}
      isSelected={task.isSelected}
      onClick={() => !task.isSelected && onClick(task.id)}
      data-testid={`relation-node-${task.id}`}
      aria-current={task.isSelected ? 'true' : undefined}
    >
      <NodeBackground width={width} height={height} rx={10} ry={10} isSelected={task.isSelected} />

      <NodeTitle x={PADDING} y={26}>
        {truncate(task.title, contentWidth, 13)}
      </NodeTitle>
      <NodeMeta x={PADDING} y={44}>
        {truncate(identifier, contentWidth, 11)}
      </NodeMeta>

      <circle cx={PADDING + 4} cy={60} r={4} fill={statusColor} />
      <NodeMeta x={PADDING + 14} y={64}>
        {truncate(task.status ? getEnumDisplayValue(task.status as WorkTaskStatus) : 'Ukendt status', contentWidth / 2, 11)}
      </NodeMeta>
      <NodeMeta x={width - PADDING} y={64} textAnchor="end">
        {truncate(task.assignedToName ?? 'Ingen tekniker', contentWidth / 2 - PADDING, 11)}
      </NodeMeta>

      <NodeMeta x={PADDING} y={82}>
        {truncate(formatDates(task.fromDate, task.toDate), contentWidth, 11)}
      </NodeMeta>
    </Group>
  );
};

const Group = styled.g<{ isSelected: boolean }>`
  cursor: ${(props) => (props.isSelected ? 'default' : 'pointer')};
`;

const NodeBackground = styled.rect<{ isSelected: boolean }>`
  fill: ${(props) => props.theme.palette.background.primary};
  stroke: ${(props) => (props.isSelected ? props.theme.palette.functions.action.primary : props.theme.palette.grey.black10)};
  stroke-width: ${(props) => (props.isSelected ? 3 : 1)};
`;

const NodeTitle = styled.text`
  font-family: ${(props) => props.theme.typography.b.fontFamily};
  font-size: 13px;
  font-weight: 600;
  fill: ${(props) => props.theme.palette.text.primary};
`;

const NodeMeta = styled.text`
  font-family: ${(props) => props.theme.typography.p.fontFamily};
  font-size: 11px;
  fill: ${(props) => props.theme.palette.grey.black60};
`;

export default RelationNode;
